"use client";

import dynamic from 'next/dynamic';
import { FC, useState } from 'react';
import { useCohesionConfigContext } from '@2u-landing-pages/providers';
import { TemplateWrapper } from "@2u-landing-pages/components/TemplateWrapper";
import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { Flex } from '@2u-landing-pages/templates/Flex';
import { Tabs } from '@2u-landing-pages/templates/Tabs';
import { Container, Split as BricksSplit } from "@red-digital/bricks";
import eventClient from '@2u-landing-pages/utils/event-client';
import cn from 'classnames';
import {
  TypeContent_fullWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { DynamicExperienceFlowProps } from './DynamicExperienceFlow.types';
// @ts-ignore
import styles from './DynamicExperienceFlow.module.scss';

const DynamicExperience = dynamic(
  () => import('@highereducation/dynamic-experience').then((mod) => ({ default: mod.DynamicExperience })),
  { ssr: false },
);

type DEFlowEnvironment = 'prod' | 'int';

export const DynamicExperienceFlow: FC<DynamicExperienceFlowProps> = ({
  name,
  flow_id,
  results,
  isLoading,
  split_layout = "50/50",
  side_content,
  phoneNumber,
  ...sharedProps
}) => {
  const cohesionConfig = useCohesionConfigContext();
  const [result, setResult] = useState('');
  const makeSourceKey = cohesionConfig.make.sourceKey;

  if (isLoading || !flow_id) {
    return null;
  }

  if (!result) {
    const deWidget = (
      <DynamicExperience
        forcedFlowId={flow_id}
        tenant={{ tagularSourceKey: makeSourceKey }}
        environment={process.env.DE_ENV as DEFlowEnvironment || "prod"}
        isFullPageExp={false}
        onResult={results?.length ? ((result) => {
          setResult(result?.id || '')
        }) : undefined}
        scoped
      />
    );

    if (split_layout && side_content) {
      const layoutClass = styles[`split_${split_layout.replace('/', '_')}`];

      return (
        <TemplateWrapper className={styles.deWrapper} padded>
          <BricksSplit
            direction="column"
            lg={{ direction: "row" }}
            className={cn(styles.splitContainer, layoutClass)}
          >
            <div className={styles.companionColumn}>
              <ContentLayout
                content={side_content as TypeContent_fullWithoutUnresolvableLinksResponse}
                phoneNumber={phoneNumber}
              />
            </div>
            <div className={styles.deColumn}>
              {deWidget}
            </div>
          </BricksSplit>
        </TemplateWrapper>
      );
    }

    return (
      <TemplateWrapper className={styles.deWrapper} padded>
        <Container>
          {deWidget}
        </Container>
      </TemplateWrapper>
    );
  }

  const matchedResult = results?.find((entry) => entry?.fields?.de_result_id === result);
  if (!matchedResult) {
    eventClient.tempMonarchDecision({
      webElement: {
        location: "Failed to find DE result to render",
        name: flow_id,
        htmlId: result,
      },
    });

    return null;
  }

  const contentType = matchedResult.sys.contentType.sys.id;
  const id = matchedResult.sys.id;

  eventClient.tempMonarchDecision({
    webElement: {
      location: "DE Result Rendered",
      name: matchedResult?.fields?.name,
      htmlId: matchedResult?.sys?.id,
    },
  });

  switch (contentType) {
    case 'flex':
      return <Flex id={id} {...(matchedResult as any).fields} {...sharedProps} />;
    case 'tabs':
      return <Tabs id={id} {...(matchedResult as any).fields} {...sharedProps} />;
    default:
      return null;
  }
};

